export type SSEHandlers = {
	onStart?: () => void;
	onDelta?: (delta: string) => void;
	onEnd?: () => void;
	onError?: (message: string) => void;
	onProgram?: (status: string, id?: string | null) => void;
	onMeal?: (status: string) => void;
};

export class SSEChat {
	protected static functionUrl: string = '';

	public static configure(functionUrl: string) {
		SSEChat.functionUrl = functionUrl;
	}

	public static start(message: string, context: any = {}, handlers: SSEHandlers = {}) {
		if (!SSEChat.functionUrl) throw new Error('SSEChat not configured: missing function URL');

		// If we have any context/history, prefer POST streaming via fetch
		// to avoid URL length limits and allow larger payloads.
		try {
			if (context && Object.keys(context).length > 0) {
				try { console.log('[SSEChat] start → fetch POST stream', { url: SSEChat.functionUrl }); } catch {}
				return SSEChat.startWithFetch(message, context, handlers);
			}
		} catch {}

		const url = new URL(SSEChat.functionUrl);
		// For small payloads, GET with query is simplest and compatible with EventSource
		url.searchParams.set('message', message);
		if (context && Object.keys(context).length > 0) {
			try { url.searchParams.set('context', JSON.stringify(context)); } catch {}
		}

		try { console.log('[SSEChat] start → EventSource GET', { url: url.toString() }); } catch {}
		const es = new EventSource(url.toString());
		let hasStarted = false;
		let hasDelta = false;
		let hasEnded = false;
		let closed = false;
		let cleanup: () => void = () => { es.close(); };


		const handleObj = (data: any) => {
			if (!data || typeof data !== 'object') return;
			if (data.event === 'start') {
				hasStarted = true;
				handlers.onStart && handlers.onStart();
				return;
			}
			if (data.event === 'delta' && typeof data.delta === 'string') {
				hasDelta = true;
				handlers.onDelta && handlers.onDelta(data.delta);
				return;
			}
			if (data.event === 'program') {
				handlers.onProgram && handlers.onProgram(String(data.status || ''), data.id || null);
				return;
			}
			if (data.event === 'meal') {
				handlers.onMeal && handlers.onMeal(String(data.status || ''));
				return;
			}
			if (data.event === 'end') {
				hasEnded = true;
				handlers.onEnd && handlers.onEnd();
				try { es.close(); } catch {}
				closed = true;
				return;
			}
			if (data.event === 'error') {
				handlers.onError && handlers.onError(String(data.message || 'error'));
				try { es.close(); } catch {}
				closed = true;
				return;
			}
		};

		// Default unnamed events
		es.onmessage = (e) => {
			try { handleObj(JSON.parse(e.data)); } catch {
				handlers.onError && handlers.onError('invalid sse data');
				try { es.close(); } catch {}
				closed = true;
			}
		};

		// Named events (start/delta/end/error)
		es.addEventListener('start', (e: MessageEvent) => { try { handleObj(JSON.parse((e as any).data)); } catch {} });
		es.addEventListener('delta', (e: MessageEvent) => { try { handleObj(JSON.parse((e as any).data)); } catch {} });
		es.addEventListener('end', (e: MessageEvent) => { try { handleObj(JSON.parse((e as any).data)); } catch {} });
		es.addEventListener('error', (e: MessageEvent) => { try { handleObj(JSON.parse((e as any).data)); } catch {} });

			es.onerror = () => {
			// Some browsers emit onerror when server closes normally.
			// Only surface as error if we never started and got no data.
			if (!hasEnded && !hasDelta && !closed) {
				// Fallback to fetch streaming so it appears in Network → Fetch/XHR
					try { console.log('[SSEChat] EventSource error → fallback to fetch'); } catch {}
				const cancel = SSEChat.startWithFetch(message, context, handlers);
				cleanup = () => { try { cancel?.(); } catch {} };
			}
			es.close();
		};

		return () => { try { cleanup(); } catch {} };
	}

	private static startWithFetch(message: string, context: any, handlers: SSEHandlers) {
		const controller = new AbortController();
		const signal = controller.signal;
		const dec = new TextDecoder();
		let buffer = '';
		let hasDelta = false;
		let hasEnded = false;
		let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

		(async () => {
			try {
				handlers.onStart && handlers.onStart();
				const res = await fetch(SSEChat.functionUrl, {
					method: 'POST',
					headers: { 'content-type': 'application/json' },
					body: JSON.stringify({ message, context }),
					signal
				});
				try { console.log('[SSEChat] fetch connected', { status: res.status }); } catch {}
				if (!res.body) throw new Error('no body');
				reader = res.body.getReader();
				for (;;) {
					const { done, value } = await reader.read();
					if (done) break;
					buffer += dec.decode(value, { stream: true });
					let idx;
						while ((idx = buffer.indexOf('\n\n')) >= 0) {
							const rawBlock = buffer.slice(0, idx);
							buffer = buffer.slice(idx + 2);
							const block = rawBlock.trim();
							if (!block) continue;
							// Parse standard SSE: may include 'event: <type>\n' and 'data: <json>' possibly across multiple lines
							let evtType: string | null = null;
							let dataPayload = '';
							for (const line of block.split('\n')) {
								const trimmed = line.trim();
								if (trimmed.startsWith('event:')) {
									evtType = trimmed.slice(6).trim();
								} else if (trimmed.startsWith('data:')) {
									dataPayload += (dataPayload ? '\n' : '') + trimmed.slice(5).trim();
								}
							}
							if (dataPayload) {
								try {
									const data = JSON.parse(dataPayload);
									const ev = (data && typeof data === 'object' && data.event) ? String(data.event) : (evtType || 'message');
									if (ev === 'delta' && typeof data.delta === 'string') { hasDelta = true; handlers.onDelta && handlers.onDelta(data.delta); }
									else if (ev === 'program') { handlers.onProgram && handlers.onProgram(String(data.status || ''), data.id || null); }
									else if (ev === 'meal') { handlers.onMeal && handlers.onMeal(String(data.status || '')); }
									else if (ev === 'end') { hasEnded = true; handlers.onEnd && handlers.onEnd(); }
									else if (ev === 'error') { handlers.onError && handlers.onError(String(data.message || 'error')); }
								} catch {}
							}
						}
				}
				if (!hasEnded && hasDelta) { handlers.onEnd && handlers.onEnd(); }
			} catch (e) {
				if (!hasEnded && !hasDelta) { handlers.onError && handlers.onError('sse connection error'); }
			} finally {
				try { await reader?.cancel(); } catch {}
			}
		})();

		return () => {
			try { controller.abort(); } catch {}
			try { reader?.cancel(); } catch {}
		};
	}
}


